import React from 'react';
import { Card, Tag, Typography, Row, Col, Tooltip, Progress } from 'antd';
import { ThunderboltOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * STATUS dos carregadores EV (mapeamento GoodWe SEMS)
 * 0 = Disponível (verde) | 1 = Carregando (azul) | 2 = Sessão finalizada (cinza) | 3 = Falha (vermelho) | null/? = Desconhecido
 */
const STATUS_MAP = {
    CONECTADO:     { label: 'Disponível',  color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', dot: '🟢' },
    CARREGANDO:    { label: 'Carregando',  color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', dot: '🔵' },
    ALERTA:        { label: 'Alerta',      color: '#faad14', bg: '#fffbe6', border: '#ffe58f', dot: '🟡' },
    FALHA:         { label: 'Falha',       color: '#ff4d4f', bg: '#fff1f0', border: '#ffa39e', dot: '🔴' },
    DESCONECTADO:  { label: 'Offline',     color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', dot: '⚫' },
};

function getStatus(status) {
    if (!status) return STATUS_MAP.DESCONECTADO;
    return STATUS_MAP[status.toUpperCase()] || STATUS_MAP.DESCONECTADO;
}

function formatPower(w) {
    if (w == null || w === 0) return { value: '0,00', unit: 'W' };
    if (w >= 1000) return { value: (w / 1000).toFixed(2).replace('.', ','), unit: 'kW' };
    return { value: parseFloat(w).toFixed(2).replace('.', ','), unit: 'W' };
}

function ConnectorBadge({ type }) {
    const label = type || 'N/D';
    const colorMap = {
        AC_TYPE1: '#722ed1', AC_TYPE2: '#531dab', DC_CCS: '#1890ff',
        DC_CHADEMO: '#fa541c', DC_GB: '#13c2c2',
    };
    return (
        <Tag color={colorMap[label] || '#595959'} style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>
            {label.replace('_', ' ')}
        </Tag>
    );
}

/**
 * EvChargerCard — Card rico para exibir dados de EV Charger (Estação de Recarga)
 *
 * Props:
 *   equipment   — dados do equipamento (nome, modelo, fabricante, status, capacity, siteId...)
 *   leitura     — última leitura de leituras_estacao_recarga (p_charge, e_charge_today, status, etc.)
 *   siteName    — nome do site
 *   analytics   — dados analíticos (sessions, totalEnergy, totalCost)
 */
const EvChargerCard = ({ equipment = {}, leitura = null, siteName = '—', analytics = null }) => {
    const status = getStatus(leitura?.status || equipment.status);
    const isCharging = (leitura?.status_code === 1) || (equipment.status === 'CARREGANDO');

    const pAtual = leitura?.p_charge ?? 0;
    const pMax = leitura?.p_max ?? equipment.potenciaKw ?? equipment.capacity ?? 0;
    const powerDisplay = formatPower(pAtual);
    const chargePercent = pMax > 0 ? Math.round((pAtual / (pMax * 1000)) * 100) : 0;

    const energyHoje = leitura?.e_charge_today ?? 0;
    const energyTotal = leitura?.e_charge_total ?? 0;
    const corrente = leitura?.current_charge;
    const tensao = leitura?.voltage_charge;
    const duracaoMin = leitura?.session_duration_min;
    const energiaSessao = leitura?.e_charge_session;
    const sessionCount = leitura?.session_count ?? analytics?.sessions ?? 0;

    return (
        <Card
            hoverable
            style={{
                borderRadius: 12,
                overflow: 'hidden',
                border: `1px solid ${status.border}`,
                boxShadow: isCharging ? `0 0 0 2px ${status.color}22, 0 4px 16px rgba(0,0,0,0.08)` : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.2s',
            }}
            bodyStyle={{ padding: 0 }}
        >
            {/* Header escuro estilo SEMS Plus */}
            <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 38, height: 38, borderRadius: '50%',
                        background: `${status.color}22`,
                        border: `2px solid ${status.color}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18,
                    }}>
                        🚗
                    </div>
                    <div>
                        <Text strong style={{ color: '#fff', fontSize: 13, display: 'block' }}>
                            {equipment.name || 'Carregador EV'}
                        </Text>
                        <Text style={{ color: '#8d9fc5', fontSize: 11 }}>
                            {equipment.manufacturer || 'GoodWe'} {equipment.model ? `· ${equipment.model}` : ''}
                        </Text>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: `${status.color}22`, border: `1px solid ${status.color}55`,
                        borderRadius: 20, padding: '2px 8px',
                    }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: status.color, animation: isCharging ? 'pulse 1.5s infinite' : 'none' }} />
                        <Text style={{ color: status.color, fontSize: 11, fontWeight: 600 }}>{status.label}</Text>
                    </div>
                    {equipment.connector_type && (
                        <div style={{ marginTop: 4 }}>
                            <ConnectorBadge type={equipment.connector_type} />
                        </div>
                    )}
                </div>
            </div>

            {/* Potência atual — destaque estilo SEMS */}
            <div style={{ background: status.bg, padding: '12px 16px', borderBottom: `1px solid ${status.border}` }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, justifyContent: 'center' }}>
                    <Title level={2} style={{ margin: 0, color: status.color, lineHeight: 1 }}>
                        {powerDisplay.value}
                    </Title>
                    <Text style={{ color: status.color, fontSize: 15, fontWeight: 600 }}>{powerDisplay.unit}</Text>
                </div>
                <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11, marginTop: 2 }}>
                    Potência de carregamento
                </Text>
                {pMax > 0 && (
                    <Tooltip title={`${pAtual.toFixed(0)} W de ${(pMax * 1000).toFixed(0)} W max`}>
                        <Progress
                            percent={Math.min(chargePercent, 100)}
                            size="small"
                            strokeColor={status.color}
                            trailColor="#e8e8e8"
                            showInfo={false}
                            style={{ marginTop: 6, marginBottom: 0 }}
                        />
                    </Tooltip>
                )}
            </div>

            {/* Dados do dia */}
            <div style={{ padding: '12px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px 4px', background: '#f6ffed', borderRadius: 6 }}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Hoje</Text>
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                {energyHoje.toFixed(2)} kWh
                            </Text>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px 4px', background: '#e6f7ff', borderRadius: 6 }}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Sessões</Text>
                            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                                {sessionCount}
                            </Text>
                        </div>
                    </Col>

                    {/* Sessão ativa — só exibe se estiver carregando */}
                    {isCharging && energiaSessao != null && (
                        <Col span={12}>
                            <div style={{ textAlign: 'center', padding: '8px 4px', background: '#fff7e6', borderRadius: 6 }}>
                                <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Sessão atual</Text>
                                <Text strong style={{ fontSize: 14, color: '#fa8c16' }}>
                                    {parseFloat(energiaSessao).toFixed(3)} kWh
                                </Text>
                            </div>
                        </Col>
                    )}
                    {isCharging && duracaoMin != null && (
                        <Col span={12}>
                            <div style={{ textAlign: 'center', padding: '8px 4px', background: '#fff7e6', borderRadius: 6 }}>
                                <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                    <ClockCircleOutlined style={{ marginRight: 2 }} />Duração
                                </Text>
                                <Text strong style={{ fontSize: 14, color: '#fa8c16' }}>
                                    {duracaoMin >= 60 ? `${Math.floor(duracaoMin / 60)}h ${duracaoMin % 60}min` : `${duracaoMin} min`}
                                </Text>
                            </div>
                        </Col>
                    )}

                    {corrente != null && (
                        <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Corrente: </Text>
                            <Text style={{ fontSize: 11 }}><strong>{parseFloat(corrente).toFixed(1)}</strong> A</Text>
                        </Col>
                    )}
                    {tensao != null && (
                        <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Tensão: </Text>
                            <Text style={{ fontSize: 11 }}><strong>{parseFloat(tensao).toFixed(0)}</strong> V</Text>
                        </Col>
                    )}
                </Row>

                {/* Rodapé com total e site */}
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        📍 {siteName}
                    </Text>
                    {energyTotal > 0 && (
                        <Tooltip title="Energia total carregada (acumulada)">
                            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                                <ThunderboltOutlined style={{ marginRight: 2 }} />
                                {energyTotal.toFixed(1)} kWh total
                            </Text>
                        </Tooltip>
                    )}
                </div>

                {leitura?.error_msg && (
                    <div style={{ marginTop: 8, padding: '6px 8px', background: '#fff1f0', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <WarningOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                        <Text style={{ fontSize: 11, color: '#ff4d4f' }}>{leitura.error_msg}</Text>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default EvChargerCard;
